/**
 * useConvexConnectionState — v.1-D §7.16, Decision D-022.
 *
 * v.1-C reached for `convex.connectionState.subscribe(...)` — that API does
 * not exist on the Convex client (fabrication F-1 from §0.4). v.1-D derives
 * the same three-state signal from primitives that DO exist:
 *
 *   `useConvexAuth().isLoading`           → 'reconnecting'
 *   `useConvexAuth().isAuthenticated`==false → 'disconnected'
 *   snapshot subscription stays undefined
 *     for > 3 seconds while authenticated  → 'reconnecting'
 *   snapshot resolves                      → 'connected'
 *
 * The 3-second debounce is load-bearing: a single render tick where the
 * snapshot is pending must NOT flip the UI into "reconnecting", or the user
 * will see the banner flash on every navigation. Only a sustained
 * pending-for-3-s window flips state.
 *
 * The effect cleanup clears any in-flight debounce timer when the snapshot
 * resolves before 3 s — that's how the timer "cancels" when data arrives.
 */
import { useEffect, useState } from 'react';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '@convex/api';

export type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

export function useConvexConnectionState(): ConnectionState {
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const snapshot = useQuery(api.channelState.getRunningChannelsSnapshot);
  const [state, setState] = useState<ConnectionState>('connected');

  useEffect(() => {
    if (authLoading) {
      setState('reconnecting');
      return;
    }
    if (!isAuthenticated) {
      setState('disconnected');
      return;
    }
    if (snapshot === undefined) {
      // Pending — flip to 'reconnecting' ONLY after a 3-second sustained
      // window. Cleanup clears the timer if the snapshot resolves first.
      const t = setTimeout(() => setState('reconnecting'), 3000);
      return () => clearTimeout(t);
    }
    setState('connected');
  }, [authLoading, isAuthenticated, snapshot]);

  return state;
}
